import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Container,
  Paper,
  IconButton,
  InputAdornment,
  useMediaQuery,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Visibility,
  VisibilityOff,
  PersonOutline,
  LockOutlined,
  LinkedIn,
  Facebook,
  Instagram,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useLoginUserMutation } from "state/api";
import { useDispatch } from "react-redux";
import { login } from "state/index";
import profileImage from "assets/cricket.jpg";
import mobilebgImage from "assets/cricketmobile.jpg";
import logoImage from "assets/logo.png"; // Make sure to import your logo

const LoginPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const [formData, setFormData] = useState({ EmpNo: "", password: "" });
  const [errors, setErrors] = useState({ EmpNo: false, password: false });
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginUser, { isLoading }] = useLoginUserMutation();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: false });
    setErrorMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      EmpNo: formData.EmpNo.trim() === "",
      password: formData.password.trim() === "",
    };

    setErrors(newErrors);
    if (newErrors.EmpNo || newErrors.password) return;

    try {
      const response = await loginUser(formData).unwrap();

      if (response?.token) {
        dispatch(
          login({
            token: response.token,
            EmpNo: response.user.id,
            MgCd: response.user.role,
            username: response.user.username,
          })
        );

        const mgCdLength = response.user.role.length;

        if (mgCdLength === 7) {
          window.location.href = "/Mr";
        } else if (mgCdLength === 6 || mgCdLength === 5) {
          window.location.href = "/Sm";
        } else if (response.user.role === "Admi") {
          window.location.href = "/Admin";
        } else {
          setErrorMessage("Unknown role");
        }
      } else {
        setErrorMessage("Invalid credentials or missing token.");
      }
    } catch (err) {
      setErrorMessage(err?.data?.message || "Login Failed");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
        backgroundImage: `
          radial-gradient(circle at center, rgba(253, 253, 253, 1) 0%, rgba(228, 237, 240, 1) 70%, rgba(12, 231, 247, 0.8) 100%),
          url(${isMobile ? mobilebgImage : profileImage})
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundBlendMode: "multiply",
      }}
    >
      <Container component="main" maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              overflow: "hidden",
              borderRadius: 4,
              boxShadow: "0 15px 30px rgba(0,0,0,0.3)",
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)"
            }}
          >
            {/* Left side - Branding */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                p: 4,
                // background: "rgba(92, 148, 201, 0.5)",
                color: "white",
                textAlign: "center",
                backdropFilter: "blur(5px)",
                borderRight: { md: "1px solid rgba(255, 255, 255, 0.1)" },
                borderBottom: { xs: "1px solid rgba(255, 255, 255, 0.1)", md: "none" }
              }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ 
                  delay: 0.4, 
                  duration: 0.6,
                  type: "spring",
                  stiffness: 260,
                  damping: 20
                }}
              >
                <Box
                  component="img"
                  src={logoImage}
                  alt="Company Logo"
                  sx={{ 
                    width: 230, 
                    height: 220,
                    borderRadius: "20%",
                    border: "0px solid rgba(255, 255, 255, 0.3)",
                    boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
                    backgroundColor: "rgba(255, 255, 255, 0.1)"
                  }}
                />
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
              >
                <Typography variant="h4" sx={{ mt: 2, fontWeight: 700, color: "white" }}>
                  Welcome Back
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, opacity: 0.9, color: "rgba(255,255,255,0.8)" }}>
                  Sign in to access your dashboard
                </Typography>
              </motion.div>
            </Box>

            {/* Right side - Login Form */}
            <Box
              sx={{
                flex: 1,
                p: 4,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                background: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(5px)",
              }}
            >
              <Typography variant="h3" sx={{ mb: 1, fontWeight: 600, color: "white" }}>
                Sign In
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: "rgba(255,255,255,0.8)" }}>
                Enter your credentials to continue
              </Typography>

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Employee ID"
                  name="EmpNo"
                  value={formData.EmpNo}
                  onChange={handleChange}
                  error={errors.EmpNo}
                  helperText={errors.EmpNo ? "Employee ID is required" : ""}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutline sx={{ color: "rgba(255,255,255,0.7)" }} />
                      </InputAdornment>
                    ),
                    sx: { 
                      borderRadius: 2,
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      color: "white",
                      '& .MuiInputBase-input': {
                        color: "white"
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: "rgba(255,255,255,0.3)"
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: "rgba(255,255,255,0.5)"
                      }
                    }
                  }}
                  InputLabelProps={{
                    style: { color: "rgba(255,255,255,0.7)" }
                  }}
                  variant="outlined"
                  placeholder="Enter your employee ID"
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  error={errors.password}
                  helperText={errors.password ? "Password is required" : ""}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: "rgba(255,255,255,0.7)" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: "rgba(255,255,255,0.7)" }}
                        >
                          {showPassword ? 
                            <VisibilityOff /> : 
                            <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { 
                      borderRadius: 2,
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      color: "white",
                      '& .MuiInputBase-input': {
                        color: "white"
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: "rgba(255,255,255,0.3)"
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: "rgba(255,255,255,0.5)"
                      }
                    }
                  }}
                  InputLabelProps={{
                    style: { color: "rgba(255,255,255,0.7)" }
                  }}
                  variant="outlined"
                  placeholder="Enter your password"
                />

                 <Box sx={{ textAlign: "right", mt: 1 }}>
                  <Button 
                    size="small" 
                    sx={{ 
                      textTransform: 'none',
                      color: "rgba(255,255,255,0.8)"
                    }}
                  >
                    Forgot password?
                  </Button>
                </Box>

                {errorMessage && (
                  <Typography 
                    color="error" 
                    variant="body2" 
                    sx={{ 
                      mt: 2,
                      p: 1,
                      backgroundColor: "rgba(255, 0, 0, 0.2)",
                      borderRadius: 1,
                      textAlign: 'center',
                      color: "#ffcccc",
                      border: "1px solid rgba(255, 0, 0, 0.3)"
                    }}
                  >
                    {errorMessage}
                  </Typography>
                )}

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isLoading}
                    sx={{
                      mt: 4,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      fontSize: 16,
                      letterSpacing: 0.5,
                      background: "linear-gradient(135deg, rgba(175, 212, 230, 0.8) 0%, rgba(18, 209, 223, 0.8) 100%)",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                      color: "white",
                      '&:hover': {
                        boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
                        transform: "translateY(-2px)",
                        background: "linear-gradient(135deg, rgba(73, 209, 233, 1) 0%, rgba(33, 10, 240, 1) 100%)",
                      },
                      '&:active': {
                        transform: "translateY(0)"
                      },
                      transition: "all 0.2s ease"
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Sign In"
                    )}
                  </Button>

                <Divider sx={{ my: 3, color: "rgba(255,255,255,0.5)" }}>Jagsonpal Social Media</Divider>

                <Box sx={{ 
                    display: "flex", 
                    justifyContent: "center", 
                    gap: 2,
                    '& button': {
                      transition: 'all 0.2s ease'
                    }
                  }}>
                    {[
                      { 
                        icon: <LinkedIn fontSize="small" />,
                        url: 'https://in.linkedin.com/company/jagsonpalpharmaceuticals', 
                        color: '#0A66C2',
                        label: 'LinkedIn'
                      },
                      { 
                        icon: <Facebook fontSize="small" />,
                        url: 'https://www.facebook.com/Jagsonpalpharma', 
                        color: '#1877F2',
                        label: 'Facebook'
                      },
                      { 
                        icon: (
                          <Box sx={{
                            background: 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)',
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Instagram sx={{ color: 'white', fontSize: 16 }} />
                          </Box>
                        ),
                        url: 'https://in.linkedin.com/company/jagsonpalpharmaceuticals', 
                        label: 'Instagram'
                      }
                    ].map((social) => (
                      <IconButton
                        key={social.url}
                        component="a"
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="medium"
                        aria-label={social.label}
                        sx={{ 
                          width: 48,
                          height: 48,
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                          backdropFilter: "blur(5px)",
                          border: "1px solid rgba(255,255,255,0.2)",
                          color: "white",
                          '&:hover': {
                            transform: "translateY(-3px)",
                            backgroundColor: "rgba(255, 255, 255, 0.2)",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.2)"
                          }
                        }}
                      >
                        {social.icon}
                      </IconButton>
                    ))}
                  </Box>
              </form>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default LoginPage;